/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import { TableUtility } from '@utility/dynamic-table';
import UseAxios from '../../../Api/http/http';
import {
  getOfferingWiseReportFilters,
  buildProjectServiceApiUrl,
} from '../../../Api/request/offeringWiseReport';
import { OFFERING_WISE_REPORT } from '../../../Api/resources/endPoint';
import { buildOfferingWiseTableConfig } from './OfferingWiseReportData';
import { useAuth } from '../../../Hooks/useAuth';

import './OfferingWiseReport.scss';

const formatCurrency = (val) => {
  if (val === undefined || val === null || Number.isNaN(val)) return '0';
  return Number(val).toLocaleString('en-IN', {
    maximumFractionDigits: 2,
  });
};

import { createPortal } from 'react-dom';
import lottie from 'lottie-web';
import PropTypes from 'prop-types';
import emptyStateAnimationData from '../../../Assets/images/json/dar_no_data.json';
import PuzzleLoader from '../../../Assets/images/svg/loading puzzle.gif';

const getStoredAuthToken = () => localStorage.getItem('authToken') || '';

const EmptyState = ({ message }) => {
  const animationContainer = useRef(null);

  useEffect(() => {
    if (animationContainer.current) {
      const anim = lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: emptyStateAnimationData,
      });
      return () => anim.destroy();
    }
    return undefined;
  }, []);

  return (
    <div className="ms-empty-state-outer">
      <div className="ms-empty-state">
        <div className="ms-empty-state-animation-card">
          <div ref={animationContainer} className="ms-empty-state-animation" />
          <p className="ms-empty-state-text">{message}</p>
        </div>
      </div>
    </div>
  );
};

EmptyState.propTypes = { message: PropTypes.string.isRequired };

const hasFilterValue = (item) => {
  if (!item || item?.type !== 'filter') return false;
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0)
    return true;
  if (Array.isArray(item?.range)) return item.range.some(Boolean);
  return Boolean(item?.selectedValue ?? item?.inputValue ?? item?.value);
};

const getAppliedFiltersFromConfiguration = (tableConfiguration = []) =>
  tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .filter(hasFilterValue);

const getCurrentFinancialYear = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-indexed (Jan = 1, Dec = 12)

  if (currentMonth >= 4) {
    return `${currentYear}-${currentYear + 1}`;
  } else {
    return `${currentYear - 1}-${currentYear}`;
  }
};

const buildGlobalFilterColumn = (field, label, template, options, defaultValue = []) => {
  const optionsMapped = Array.isArray(options)
    ? options.map((opt) =>
      typeof opt === 'object' ? opt : { label: opt, value: opt },
    )
    : [];

  const filterObj = {
    type: 'filter',
    field,
    label,
    placeholder: `Select ${label}`,
    template,
    selectedValues: defaultValue,
    selectedValue: defaultValue[0] || null,
    options: optionsMapped,
    values: optionsMapped,
    ...(field === 'offeringIds' ? {
      isRequired: true,
      requiredOnMainApply: true,
      requiredMessage: 'Offering is a mandatory filter.',
    } : {}),
  };

  return {
    field,
    header: label,
    width: 150,
    filter: true,
    visible: false,
    sortable: false,
    pinnable: false,
    defaultPinned: false,
    isGlobalFilter: true,
    showFilterButtonInHeader: false,
    showPinButtonInHeader: false,
    menuItems: [
      {
        label,
        items: [filterObj],
      },
    ],
  };
};

const OfferingWiseReport = () => {
  const { userProfile } = useAuth();
  const authToken = userProfile?.access_token || getStoredAuthToken();
  const [isLoading, setIsLoading] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);

  const userInfo = useMemo(() => ({
    id: userProfile?.employeeId,
    emailId: userProfile?.email || '',
    email: userProfile?.email || '',
    emp_id: userProfile?.employeeId || userProfile?.emp_id || '',
    employeeId: userProfile?.employeeId || '',
    employee_id: userProfile?.employeeId || '',
    permissions: userProfile?.permissions || {},
    roleDetails: Array.isArray(userProfile?.roleDetails) ? userProfile.roleDetails : [],
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [
    userProfile?.employeeId,
    userProfile?.email,
    userProfile?.emp_id,
    userProfile?.permissions,
    userProfile?.roleDetails,
    userProfile?.siteConfiguration,
  ]);

  const tableContainerRef = useRef(null);
  const widgetsHostRef = useRef(null);
  const [widgetsMounted, setWidgetsMounted] = useState(false);

  // Filters options fetched from API
  const [filterOptions, setFilterOptions] = useState({
    offerings: [],
    architects: [],
  });
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // Report data for widgets
  const [reportData, setReportData] = useState({
    details: {
      revenueTarget: 0,
      invoiceAchieved: 0,
      totalProjects: 0,
      inProgress: 0,
    },
    srArchitects: [],
    architects: [],
    tableData: [],
  });

  // Fetch filter dropdown options on mount (or when userInfo is ready)
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setIsLoading(true);
        const res = await UseAxios(getOfferingWiseReportFilters(userInfo));
        if (res?.data?.success) {
          setFilterOptions(res.data.data || { offerings: [], architects: [] });
        } else {
          toast.error(res?.data?.message || 'Failed to fetch filters');
        }
      } catch (err) {
        toast.error('Error fetching filter dropdowns');
      } finally {
        setIsLoading(false);
        setFiltersLoaded(true);
      }
    };
    fetchFilters();
  }, [userInfo]);

  // DOM observer to inject widgets into TableUtility
  useEffect(() => {
    const rootNode = tableContainerRef.current;
    if (!rootNode) return;

    let debounceTimer = null;
    const runEnhancements = () => {
      const tableElement = rootNode.querySelector('.table-container');
      if (tableElement && !widgetsHostRef.current) {
        const host = document.createElement('div');
        host.className = 'offering-wise-widgets-host';
        tableElement.parentNode.insertBefore(host, tableElement);
        widgetsHostRef.current = host;
        setWidgetsMounted(true);
      }
    };

    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(runEnhancements, 50);
    });
    observer.observe(rootNode, { childList: true, subtree: true });
    runEnhancements();

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
      if (widgetsHostRef.current && widgetsHostRef.current.parentNode) {
        widgetsHostRef.current.parentNode.removeChild(widgetsHostRef.current);
      }
      widgetsHostRef.current = null;
      setWidgetsMounted(false);
    };
  }, []);

  const handleOfferingWiseApiResponse = useCallback(
    (response) => {
      if (!response || typeof response !== 'object') return response;

      const resData = response.data?.data || response.data || {};

      setReportData({
        details: resData.details || {},
        srArchitects: resData.srArchitects || [],
        architects: resData.architects || [],
        tableData: resData.tableData || [],
      });

      const tableData = resData.tableData || [];

      const offeringsList = filterOptions.offerings || [];
      const architectsList = filterOptions.architects || [];

      // Extract the request payload sent to the API so we can read back
      // the user's actual filter selections and preserve them when rebuilding
      // the filter columns. Without this, every response would reset the
      // financial year (and other filters) back to hardcoded defaults.
      let requestPayload = {};
      const requestData = response?.config?.data;
      if (requestData) {
        if (typeof requestData === 'string') {
          try {
            requestPayload = JSON.parse(requestData);
          } catch {
            requestPayload = {};
          }
        } else if (typeof requestData === 'object') {
          requestPayload = requestData;
        }
      }

      // Resolve the filter array from the request (TableUtility sends either
      // payload.filter or payload.filters depending on version)
      const filterArray = Array.isArray(requestPayload?.filter)
        ? requestPayload.filter
        : Array.isArray(requestPayload?.filters)
          ? requestPayload.filters
          : [];

      // Defaults — financial year falls back to current year if not in payload
      let selectedOfferings = [];
      let selectedArchitect = [];
      let selectedFinancialYear = [getCurrentFinancialYear()];

      filterArray.forEach((filterItem) => {
        // Normalise values: support selectedValues, value, selectedValue, inputValue
        const extractValues = (filterObj) => {
          if (filterObj.field === 'financialYear' && filterObj.selectedValue) {
            return [filterObj.selectedValue];
          }
          if (Array.isArray(filterObj.selectedValues) && filterObj.selectedValues.length > 0) return filterObj.selectedValues;
          if (Array.isArray(filterObj.value) && filterObj.value.length > 0) return filterObj.value;
          if (filterObj.value) return [filterObj.value];
          if (filterObj.selectedValue) return [filterObj.selectedValue];
          if (filterObj.inputValue) return [filterObj.inputValue];
          return [];
        };

        const rawValues = extractValues(filterItem);
        // Unwrap object values ({ value, label }) to their primitive value
        const normalised = rawValues.map((val) =>
          typeof val === 'object' && val !== null ? (val.value ?? val.label ?? val) : val,
        );

        if (filterItem.field === 'offeringIds' && normalised.length > 0) {
          selectedOfferings = normalised;
        } else if (filterItem.field === 'architectId' && normalised.length > 0) {
          selectedArchitect = normalised;
        } else if (filterItem.field === 'financialYear' && normalised.length > 0) {
          selectedFinancialYear = normalised; // ← preserves user-chosen year
        }
      });

      const globalFilterCols = [
        buildGlobalFilterColumn(
          'offeringIds',
          'Offering',
          'multiSelect',
          offeringsList,
          selectedOfferings,
        ),
        buildGlobalFilterColumn(
          'architectId',
          'Architect',
          'singleSelect',
          architectsList,
          selectedArchitect,
        ),
        buildGlobalFilterColumn(
          'financialYear',
          'Financial',
          'financialYearCalendar',
          [],
          selectedFinancialYear,
        ),
      ];

      const dataCols = buildOfferingWiseTableConfig().map((col) => ({
        ...col,
        sortable: false,
        isGlobalFilter: false,
        visible: true,
      }));

      response.data = {
        tableConfiguration: [...globalFilterCols, ...dataCols],
        tableData,
        meta: {
          page: 1,
          limit: 10,
          total: tableData.length,
        },
      };

      return response;
    },
    [filterOptions],
  );

  const initialGlobalFilterColumns = useMemo(
    () => [
      buildGlobalFilterColumn(
        'offeringIds',
        'Offering',
        'multiSelect',
        filterOptions.offerings || [],
      ),
      buildGlobalFilterColumn(
        'architectId',
        'Architect',
        'singleSelect',
        filterOptions.architects || [],
      ),
      buildGlobalFilterColumn(
        'financialYear',
        'Financial',
        'financialYearCalendar',
        [],
        [getCurrentFinancialYear()],
      ),
    ],
    [filterOptions.offerings, filterOptions.architects],
  );

  const offeringWiseApiUrl = buildProjectServiceApiUrl(OFFERING_WISE_REPORT);

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL: offeringWiseApiUrl,
        showToolBar: true,
        showPagination: true,
        showTieredMenu: true,
        userInfo,
        headers: { token: authToken },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => 'Offering_Wise_Report',
            sheetName: () => 'Offering Wise Report',
          },
        },
        handler: {
          transformPayload: (payload) => {
            if (Array.isArray(payload?.filter)) {
              payload.filter.forEach((filterItem) => {
                if (filterItem.field === 'financialYear' && filterItem.selectedValue) {
                  filterItem.selectedValues = [filterItem.selectedValue];
                }
              });
            }
            return payload;
          },
          handleFilteredApiResponse: handleOfferingWiseApiResponse,
          setTableStateHandler: (getTableState) => {
            const tableState = getTableState?.();
            if (Array.isArray(tableState?.tableConfiguration)) {
              tableState.tableConfiguration.forEach((col) => {
                if (Array.isArray(col?.menuItems)) {
                  col.menuItems.forEach((menu) => {
                    if (Array.isArray(menu?.items)) {
                      menu.items.forEach((item) => {
                        if (item?.field === 'financialYear' && item?.selectedValue) {
                          item.selectedValues = [item.selectedValue];
                        }
                      });
                    }
                  });
                }
              });
            }
            const configFilters = getAppliedFiltersFromConfiguration(
              tableState?.tableConfiguration || [],
            );
            const stateFilters = Array.isArray(tableState?.filters)
              ? tableState.filters.filter(hasFilterValue)
              : [];
            const appliedFilters
              = stateFilters.length > 0 ? stateFilters : configFilters;
            const nextHasApplied = appliedFilters.some((filterItem) => filterItem.field === 'offeringIds');
            setHasAppliedFilters((prev) =>
              prev === nextHasApplied ? prev : nextHasApplied,
            );
          },
        },
        toolBarSettings: {
          showAddNew: false,
          showDownloadExcel: true,
          showColumnCustomize: false,
          showColumnFilterMenu: true,
          showHelp: false,
          showSearch: false,
          showToolBarHeading: true,
          toolBarHeading: 'Offering Wise Report',
          downloadExcelFileName: () => 'Offering_Wise_Report',
          downloadExcelSheetName: () => 'Offering Wise Report',
          showSaveView: false,
          showReset: false,
          showExpandCollapse: false,
          showFilterOption: true,
          showFilters: true,
        },
      },
      tableSettings: {
        size: 'small',
        pageSize: 10,
        tableConfig: [
          ...initialGlobalFilterColumns,
          ...buildOfferingWiseTableConfig().map((col) => ({
            ...col,
            sortable: false,
            isGlobalFilter: false,
            visible: true,
          })),
        ],
        tableScrollHeight: '400px',
        loaderSkeleton: { rows: 10, bodyCells: 2, columns: 3 },
        paginationConfig: { page: 1, limit: 10, rowsPerPageOptions: [10] },
        showExpandCollapse: false,
      },
    }),
    [offeringWiseApiUrl, userInfo, authToken, handleOfferingWiseApiResponse, initialGlobalFilterColumns],
  );

  const { details, srArchitects, architects } = reportData;

  const renderWidgetsContent = () => {
    return (
      <div className="report-results-container">
        <div className="widgets-grid">
          {/* Details Widget */}
          <div className="widget-card details-card">
            <h3 className="widget-title">Details</h3>
            <div className="widget-content">
              <div className="detail-row">
                <span className="label">Revenue Target (INR)</span>
                <span className="value">
                  {formatCurrency(details.revenueTarget)}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Invoice Achieved</span>
                <span className="value">
                  {formatCurrency(details.invoiceAchieved)}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">Total Projects</span>
                <span className="value">{details.totalProjects || 0}</span>
              </div>
              <div className="detail-row">
                <span className="label">In Progress</span>
                <span className="value">{details.inProgress || 0}</span>
              </div>
            </div>
          </div>

          {/* Sr. Architects Widget */}
          <div className="widget-card list-card">
            <h3 className="widget-title">Sr. Architects</h3>
            <div className="widget-content scrollable">
              {srArchitects.length > 0 ? (
                srArchitects.map((arc) => (
                  <div key={arc.id} className="list-item">
                    <span className="name">{arc.name || '-'}</span>
                    <span className="count">{arc.projectCount ?? 0}</span>
                  </div>
                ))
              ) : (
                <div className="no-items">-</div>
              )}
            </div>
          </div>

          {/* Architects Widget */}
          <div className="widget-card list-card">
            <h3 className="widget-title">Architects</h3>
            <div className="widget-content scrollable">
              {architects.length > 0 ? (
                architects.map((arc) => (
                  <div key={arc.id} className="list-item">
                    <span className="name">{arc.name || '-'}</span>
                    <span className="count">{arc.projectCount ?? 0}</span>
                  </div>
                ))
              ) : (
                <div className="no-items">-</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`offering-wise-report-outer ${hasAppliedFilters ? '' : 'awaiting-filters'}`}
    >
      {isLoading && (
        <div className="loader-container">
          <img
            src={PuzzleLoader}
            alt="Loading..."
            style={{ width: '100px', height: 'auto' }}
          />
        </div>
      )}

      {/* Table Utility Container */}
      <div ref={tableContainerRef} className="table-utility-wrapper">
        {filtersLoaded && (
          <TableUtility configuration={configuration} showFilterOption />
        )}
        {!hasAppliedFilters && (
          <EmptyState message="Select the filters above to see the results" />
        )}
        {hasAppliedFilters
          && widgetsMounted
          && widgetsHostRef.current
          && createPortal(renderWidgetsContent(), widgetsHostRef.current)}
      </div>
    </div>
  );
};

export default OfferingWiseReport;
